import { useMemo } from 'react'
import contentTypeUtils from '../../contentTypeUtils.js'
import { apiSaveIdea, apiUpdateIdea, apiDeleteIdea } from '../studioApi.js'
import { useStudioState } from '../contexts/StudioStateContext'
import type { DeleteIdeaResponse, Idea, StateSetter } from '../types'

const { normalizeIdea } = contentTypeUtils

export interface IdeaHandlersApi {
  saveIdea: (idea: Idea) => Promise<Idea>
  updateIdea: (id: string, updates: Partial<Idea>) => Promise<Idea>
  deleteIdea: (id: string) => Promise<DeleteIdeaResponse>
}

const DEFAULT_API: IdeaHandlersApi = Object.freeze({
  saveIdea: apiSaveIdea,
  updateIdea: apiUpdateIdea,
  deleteIdea: apiDeleteIdea,
})

export interface IdeaHandlersDeps {
  setIdeas: StateSetter<Idea[]>
  setArchivedIdeas: StateSetter<Idea[]>
  api?: IdeaHandlersApi
}

export interface IdeaHandlers {
  handleSaveIdea: (idea: Idea) => Promise<Idea>
  handleUpdateIdea: (id: string, updates: Partial<Idea>) => Promise<Idea>
  handleDeleteIdea: (id: string) => Promise<void>
  handleRestoreIdea: (id: string) => Promise<Idea>
}

// Pure factory for idea CRUD handlers. Same factory + hook split as
// `usePostHandlers` (Wave 12 Phase A) — see that file for the reasoning.
//
// Idea handlers are simpler than post handlers: no notion sync, no
// integration gating, no cross-state effects. The factory exists for
// test-without-React parity with the rest of the hooks/* directory.
export function createIdeaHandlers(deps: IdeaHandlersDeps): IdeaHandlers {
  const { setIdeas, setArchivedIdeas, api = DEFAULT_API } = deps

  async function handleSaveIdea(idea: Idea): Promise<Idea> {
    const savedIdea = await api.saveIdea(idea)
    const normalized = normalizeIdea(savedIdea) as Idea
    setIdeas((prev) => [
      normalized,
      ...prev.filter((item) => item.id !== savedIdea.id),
    ])
    return normalized
  }

  async function handleUpdateIdea(
    id: string,
    updates: Partial<Idea>
  ): Promise<Idea> {
    const updatedIdea = await api.updateIdea(id, updates)
    const normalized = normalizeIdea(updatedIdea) as Idea
    setIdeas((prev) => prev.map((item) => (item.id === id ? normalized : item)))
    return normalized
  }

  async function handleDeleteIdea(id: string): Promise<void> {
    const archived = await api.deleteIdea(id)
    if (archived?.idea) {
      const normalized = normalizeIdea(archived.idea) as Idea
      setIdeas((prev) => prev.filter((i) => i.id !== id))
      setArchivedIdeas((prev) => [
        normalized,
        ...prev.filter((item) => item.id !== id),
      ])
    }
  }

  async function handleRestoreIdea(id: string): Promise<Idea> {
    const restored = await api.updateIdea(id, { archivedAt: null })
    const normalized = normalizeIdea(restored) as Idea
    setArchivedIdeas((prev) => prev.filter((item) => item.id !== id))
    setIdeas((prev) => [normalized, ...prev.filter((item) => item.id !== id)])
    return normalized
  }

  return {
    handleSaveIdea,
    handleUpdateIdea,
    handleDeleteIdea,
    handleRestoreIdea,
  }
}

export function useIdeaHandlers(): IdeaHandlers {
  const { setIdeas, setArchivedIdeas } = useStudioState()
  return useMemo(
    () => createIdeaHandlers({ setIdeas, setArchivedIdeas }),
    [setIdeas, setArchivedIdeas]
  )
}

export const __test__ = { DEFAULT_API }
